import { APIBaseURL, getVerifiedRiskToken } from '@web3/utils';
import { ProviderRpcError } from 'eip1193-types';

class WalletApiRequestException extends Error {
  code: string;
  data: Record<string, any>;
  constructor(message: string, code: string, data: Record<string, any>) {
    super(message);
    this.code = code;
    this.data = data;
  }
}

export const logoutCodes = [10000, 10007, 33003, 10009, 30000];

export type SignTransactionParams = {
  fromAddress: string;
  toAddress: string;
  chainCode: string;
  amount: string;
  gasLimit: string;
  gasPriorityFee: string;
  data: string;
  maxPriceFee: string;
  extra?: string;
  broadcast?: number;
  extData?: Record<string, any>;
};

// 为了保持模块的独立性，就不使用外面的request，这里单独就依赖一个 API的域名 base ApplicationGlobalConfig.baseURL
const webWalletApiFetch = async (
  path: string,
  {
    method = 'GET',
    query,
    body,
    headers,
  }: {
    method?: 'GET' | 'POST';
    query?: Record<string, string>;
    body?: Record<string, unknown>;
    onLogout?: () => void;
    headers?: Record<string, string>;
  } = {},
) => {
  const search = query ? new URLSearchParams(query).toString() : '';
  const url = `${APIBaseURL}${path}${search ? `?${search}` : ''}`;
  const res = await fetch(url, {
    method,
    body: method === 'POST' && body ? JSON.stringify(body) : undefined,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
  });
  const data = await res.json();
  const code = data.ret_code ?? data.retCode;
  if (res.status >= 200 && res.status < 300 && code === 0) {
    return data.result;
  }
  const error = new WalletApiRequestException(data.ret_msg || data.retMsg || res.statusText, data.ret_code ?? 1e8, data.result);
  throw error;
};

export type SignMessageResult = {
  txnId: string;
  sign: string;
};

/**
 * 离线签名类型
 */
export enum SignTypeEnum {
  ETH_SIGN = 1,
  PERSONAL = 2,
  V1 = 101,
  V3 = 103,
  V4 = 104,
}

export type SignMessageParameter = {
  fromAddress: string;
  message: string;
  chainCode: string;
  signType: SignTypeEnum;
};

export enum eip1193ErrorCode {
  userReject = 4001,
  unauthorized = 4100,
  unsupportedMethod = 4200,
  serverError = 8000,
  disconnected = 4900,
  chainDisconnected = 4901,
}
const serverErrorMsg = 'Server error please try again later.';

export const handleApiError = (err: any, eip1193Code: eip1193ErrorCode, message: string) => {
  console.error(err, eip1193Code, message);
  const code = err?.code;
  const isLogout = logoutCodes.includes(code);
  const error = new Error(isLogout ? 'Unauthorized because user has logged out' : message) as ProviderRpcError;
  error.code = isLogout ? eip1193ErrorCode.unauthorized : eip1193Code;
  error.data = err;
  throw error;
};

export const signMessage = async (params: SignMessageParameter) => {
  return await webWalletApiFetch('/spot/api/web3/wallet/eth/signMsg', {
    method: 'POST',
    body: params,
  })
    .then((data: SignMessageResult) => {
      if (!data.sign) {
        return handleApiError(
          {
            params: params,
            response: data,
          },
          eip1193ErrorCode.serverError,
          'Invalid signature',
        );
      }
      return data.sign;
    })
    .catch((err) => handleApiError(err, eip1193ErrorCode.serverError, serverErrorMsg));
};

type GasInfoParams = {
  contractAddress: string;
  fromAddress: string;
  toAddress: string;
  chainId: number;
  chainCode: string;
  walletType: 'CLOUD';
  amount: string;
  txData: string;
  isL1GasOnly?: boolean;
};

type GasInfoItem = {
  gasPriorityFee: string;
  avgTime: string;
  suggestFee: string;
  gasPrice: string;
  gasFee: string;
  maxGasPrice: string;
  maxGasFee: string;
};

type GasInfo = {
  slowGasPrice: GasInfoItem;
  middleGasPrice: GasInfoItem;
  fastGasPrice: GasInfoItem;
  suggestBaseFee: string;
  gasLimit: string;
};

export const getGasFee = (params: GasInfoParams): Promise<GasInfo> =>
  webWalletApiFetch('/web3/wallet/v1/private/mgmt/gas/fee', {
    method: 'POST',
    body: params,
  }).catch((err) => handleApiError(err, eip1193ErrorCode.serverError, serverErrorMsg));

export const sendTransaction = async (params: SignTransactionParams) => {
  let riskToken: string;
  const send = async (): Promise<{ txnId: string }> => {
    return await webWalletApiFetch('/spot/api/web3/wallet/eth/sign', {
      method: 'POST',
      body: params,
      headers: riskToken
        ? {
            riskToken,
          }
        : undefined,
    }).catch(async (err: any) => {
      if (err?.code === 32000) {
        // 需要二次验证
        if (!riskToken) {
          riskToken = await getVerifiedRiskToken({
            token: err.data.riskToken,
            username: '',
            country_code: '',
          });
          if (riskToken) {
            return await send();
          } else {
            return handleApiError(params, eip1193ErrorCode.userReject, 'The user rejected the request.');
          }
        } else {
          // 已经二次验证，但是还是错误，这里先做一个区分，避免无限循环。
          return handleApiError(err, eip1193ErrorCode.serverError, serverErrorMsg);
        }
      } else {
        return handleApiError(err, eip1193ErrorCode.serverError, serverErrorMsg);
      }
    });
  };
  const { txnId } = await send();
  const { txHash } = await pollForApiResult(
    () => getTxnResult(txnId),
    (data: any) => Boolean(data?.txHash),
  ).catch((err) => handleApiError(err, eip1193ErrorCode.serverError, serverErrorMsg));
  // console.log('tx', txHash);
  return txHash as string;
};

const getTxnResult = async (txnId: string) => {
  const res = await webWalletApiFetch('/spot/api/web3/wallet/eth/sign/result', {
    query: {
      txnId,
    },
  });
  return res;
};

const pollForApiResult = async (fn: () => any, check?: (data: any) => boolean, interval = 2e3, timeout = 30e3) => {
  const sleep = (t: number) => new Promise((resolve) => setTimeout(resolve, t));
  const start = Date.now();
  while (Date.now() - start < timeout) {
    const rs = await fn();
    if (rs === undefined || rs === null || (check && !check(rs))) {
      await sleep(interval);
    } else {
      return rs;
    }
  }
  throw new Error('poll result timeout');
};
